import { ThemeEvents } from '@theme/events';

/** CJ 5.1.5 · Read-only cart subscriber. Never intercepts fetch, mutates the
 * cart, estimates added totals, or emits a second cart:update event.
 * Amounts use the same cart.total_price basis as the existing drawer booster.
 */
class CJCartAnnouncement extends HTMLElement {
  connectedCallback() {
    this._listeners?.abort();
    this._listeners = new AbortController();
    const options = { signal: this._listeners.signal };
    this._version = (this._version || 0) + 1;
    this._info = this.querySelector('[data-cj-ann-info]');
    this._status = this.querySelector('[data-cj-ann-status]');
    this._message = this.querySelector('[data-cj-ann-message]');
    this._track = this.querySelector('[data-cj-ann-track]');
    this._fill = this.querySelector('[data-cj-ann-fill]');
    this._threshold = Number(this.dataset.threshold);
    this._enabled = this.dataset.shippingEnabled === 'true' && Number.isSafeInteger(this._threshold) && this._threshold > 0;
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._motion.addEventListener('change', this._motionChange, options);
    this._observer?.disconnect();
    this._observer = new ResizeObserver(this._measure);
    this._observer.observe(this);
    this._measure();
    this._snapshot = this._readSnapshot({
      item_count: Number(this.dataset.count), total_price: Number(this.dataset.total), currency: this.dataset.currency,
    });
    if (this._snapshot) this._apply(this._snapshot, false);
    if (this.dataset.rotating === 'true') {
      customElements.whenDefined('announcement-bar-component').then(() => { if (this.isConnected) this._syncOriginal(); });
    }
    if (!this._enabled) return;
    document.addEventListener(ThemeEvents.cartUpdate, this._onCart, options);
    document.addEventListener(ThemeEvents.discountUpdate, this._onCart, options);
    // Recovery only: a failed/partial mutation response can still change a cart.
    document.addEventListener(ThemeEvents.cartError, this._onError, options);
    document.addEventListener('visibilitychange', this._onVisible, options);
    window.addEventListener('pageshow', this._onPageShow, options);
    window.addEventListener('focus', this._onFocus, options);
    // Hydrate from Liquid immediately, then reconcile any event missed during load.
    this._scheduleRefresh(false, false);
  }

  disconnectedCallback() {
    this._version += 1;
    this._listeners?.abort();
    this._request?.abort();
    this._observer?.disconnect();
    clearTimeout(this._refreshTimer);
    clearTimeout(this._burstTimer);
    clearTimeout(this._requestTimer);
    this.querySelector('.cj-announcement__confetti')?.remove();
    this._measure();
  }

  _readSnapshot(value) {
    if (!value || !Number.isSafeInteger(value.item_count) || value.item_count < 0 ||
        !Number.isSafeInteger(value.total_price) || value.total_price < 0 ||
        typeof value.currency !== 'string' || !/^[A-Z]{3}$/.test(value.currency)) return null;
    return { item_count: value.item_count, total_price: value.total_price, currency: value.currency };
  }

  _onCart = (event) => {
    const snapshot = this._readSnapshot(event.detail?.resource);
    const celebrate = !event.detail?.data?.didError;
    if (snapshot) {
      ++this._version;
      this._request?.abort();
      clearTimeout(this._requestTimer);
      clearTimeout(this._refreshTimer);
      this._apply(snapshot, celebrate);
    } else {
      this._scheduleRefresh(celebrate, true);
    }
  };
  _onError = () => this._scheduleRefresh(false, true);
  _onVisible = () => { if (!document.hidden) this._scheduleRefresh(false, false); };
  _onPageShow = (event) => { if (event.persisted) this._scheduleRefresh(false, false); };
  _onFocus = () => this._scheduleRefresh(false, false);
  _motionChange = () => { if (this._motion.matches) this.querySelector('.cj-announcement__confetti')?.remove(); };

  _scheduleRefresh(celebrate, mutation) {
    if (!this._enabled || !this.isConnected) return;
    const version = ++this._version;
    clearTimeout(this._refreshTimer);
    clearTimeout(this._requestTimer);
    this._request?.abort();
    // Keep the last confirmed snapshot, but don't claim free shipping while the
    // new cart is unknown. Never replace it with zero on network failure.
    if (mutation) this._unknown('Actualizando tu envío…');
    this._refreshTimer = setTimeout(() => this._refresh(version, celebrate, mutation), 80);
  }

  async _refresh(version, celebrate, mutation) {
    if (version !== this._version || !this.isConnected) return;
    const controller = new AbortController();
    this._request = controller;
    this._requestTimer = setTimeout(() => controller.abort(), 8000);
    try {
      const fallback = `${window.Shopify?.routes?.root || '/'}cart.js`;
      const url = new URL(this.dataset.cartUrl || fallback, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Unexpected cart origin');
      const response = await fetch(url.href, {
        credentials:'same-origin', headers:{ Accept:'application/json' }, cache:'no-store', signal:controller.signal,
      });
      if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
      const snapshot = this._readSnapshot(await response.json());
      if (!snapshot) throw new Error('Incomplete cart snapshot');
      if (version === this._version && this.isConnected) this._apply(snapshot, celebrate);
    } catch (_) {
      if (version === this._version && this.isConnected && (mutation || this._snapshot?.item_count > 0 || this.dataset.mode === 'unknown')) {
        this._unknown('Consulta el progreso de tu envío en el carrito.');
      }
    } finally {
      if (this._request === controller) {
        clearTimeout(this._requestTimer);
        this._request = null;
      }
    }
  }

  _formatRemaining(cents) {
    // No '$0' remaining for a nonzero fraction of a peso. Match Liquid ceil.
    return new Intl.NumberFormat('es-CO', {
      style:'currency', currency:'COP', minimumFractionDigits:0, maximumFractionDigits:0,
    }).format(Math.ceil(cents / 100)).replace(/\s+/g, '');
  }

  _apply(snapshot, celebrate) {
    const wasComplete = this._confirmedComplete === true;
    const complete = this._enabled && snapshot.currency === 'COP' && snapshot.item_count > 0 && snapshot.total_price >= this._threshold;
    let mode = 'info';
    let text = '';
    if (this._enabled && snapshot.item_count > 0) {
      if (snapshot.currency !== 'COP') {
        mode = 'currency';
        text = this.dataset.currencyMessage || 'Consulta las condiciones de envío al finalizar la compra.';
      } else if (complete) {
        mode = 'complete';
        text = this.dataset.success || '¡Tu compra tiene envío gratis!';
      } else {
        mode = 'progress';
        const amount = this._formatRemaining(this._threshold - snapshot.total_price);
        const template = this.dataset.pending || 'Te faltan [amount] para envío gratis';
        text = template.includes('[amount]') ? template.replaceAll('[amount]', amount) : `${template} ${amount}`;
      }
    }
    this._snapshot = snapshot;
    this._confirmedComplete = complete;
    this.dataset.count = String(snapshot.item_count);
    this.dataset.total = String(snapshot.total_price);
    this.dataset.currency = snapshot.currency;
    this._setMode(mode, text);
    if (this._fill) this._fill.style.transform = `scaleX(${Math.max(0, Math.min(1, snapshot.total_price / this._threshold)) || 0})`;
    if (celebrate && complete && !wasComplete) this._celebrate();
  }

  _unknown(text) {
    this._setMode('unknown', text);
  }
  _setMode(mode, text) {
    this.dataset.mode = mode;
    if (this._info) this._info.hidden = mode !== 'info';
    if (this._status) this._status.hidden = mode === 'info';
    if (this._message && this._message.textContent !== text) this._message.textContent = text;
    if (this._track) this._track.hidden = !['progress','complete'].includes(mode);
    this._syncOriginal();
  }
  _syncOriginal() {
    const original = this.querySelector('announcement-bar-component');
    if (!original || typeof original.pause !== 'function') return;
    if (this.dataset.mode !== 'info') original.pause();
    else if (original.paused) original.play();
  }

  _measure = () => {
    if (!document.body) return;
    const bars = [...document.querySelectorAll('#header-group cj-cart-announcement')];
    const height = Math.round(bars.reduce((sum,bar) => sum + bar.getBoundingClientRect().height, 0));
    if (bars.length) document.body.style.setProperty('--cj-announcement-height', `${height}px`);
    else document.body.style.removeProperty('--cj-announcement-height');
  };

  _celebrate() {
    if (this.dataset.confetti !== 'true' || this._motion.matches || document.hidden) return;
    // Confetti has no focus targets, never intercepts clicks and self-removes.
    clearTimeout(this._burstTimer);
    this.querySelector('.cj-announcement__confetti')?.remove();
    const burst = document.createElement('div');
    burst.className = 'cj-announcement__confetti'; burst.setAttribute('aria-hidden','true');
    const spread = Math.min(this.clientWidth * .4, 230);
    const colors = ['#6b4d4c','#b99377','#d9bda5','#d5a6bd'];
    for (let i = 0; i < 24; i++) {
      const piece = document.createElement('i');
      piece.style.setProperty('--x', `${(Math.random() * 2 - 1) * spread}px`);
      piece.style.setProperty('--y', `${18 + Math.random() * 60}px`);
      piece.style.setProperty('--rotation', `${Math.random() * 500 - 250}deg`);
      piece.style.setProperty('--delay', `${Math.random() * 120}ms`);
      piece.style.setProperty('--piece-color', colors[i % colors.length]);
      burst.append(piece);
    }
    this.append(burst);
    this._burstTimer = setTimeout(() => burst.remove(), 1300);
  }
}

if (!customElements.get('cj-cart-announcement')) customElements.define('cj-cart-announcement', CJCartAnnouncement);
